import { Model } from '@vuex-orm/core'
import { Cloud } from 'src/store/models/channel/index.js'
import { FileParent } from 'src/store/models/pivot/index.js'
import Channels from 'src/api/channels.js'
import sentryConfig from 'src/store/sentry.js'

export class File extends Model {
  static entity = 'files'
  static primaryKey = 'uuid'
  static changeCollaborationURL = ({ cloud_id, uuid }) => `/channels/cloud/${cloud_id}/files/${uuid}/change`

  static fields () {
    return {
      id: this.attr(null),
      uuid: this.attr(null),
      user_id: this.attr(''),
      name: this.string(null),
      description: this.attr(''),
      path: this.attr(''),
      meta: this.attr({}),
      user: this.attr({}),
      reserved: this.attr(null),
      extension: this.attr(''),
      type: this.attr(''),
      hash: this.attr(''),
      size: this.attr(''),
      duration_in_seconds: this.attr(''),
      alt_name: this.attr(''),
      thumbnail: this.attr(''),
      file_type: this.attr(''),
      is_protected_folder: this.attr(null),
      is_collaborative: this.attr(null),
      upload_limited_at: this.attr(null),
      parent_id: this.attr(null),
      expires_at: this.attr(''),
      created_at: this.attr(''),
      updated_at: this.attr(''),
      cloud: this.belongsTo(Cloud, 'cloud_id'),
      cloud_id: this.attr(null),
      child_pivot: this.hasMany(FileParent, 'file_id'),
      children: this.belongsToMany(File, FileParent, 'parent_id', 'file_id'),
      parent: this.belongsTo(File, 'parent_id', 'uuid'),
      can: this.attr({})
    }
  }

  static async getRoot (id, path = '') {
    const res = await Channels.getChannelCloudByIdFilesByPath(id, path, { showMessages: false })
    return File.insertOrUpdate({ data: res.data })
  }

  static async createFolder (id, path, name, is_protected_folder, parent, upload_limited_at) {
    const res = await Channels.postChannelCloudByIdCreateDir(id, { parent, name, is_protected_folder, upload_limited_at })
    File.insertOrUpdate({ data: res.data })
  }

  static async createCloudFile (id, name, parent, type) {
    const { data } = await Channels.postChannelCloudByIdCreate(id, { name, parent, type })
    File.insertOrUpdate({ data })
  }

  static async getDetails (uuid, cloudId, attachmentId, attachmentSourceId) {
    if (cloudId) return this.getCloudFileDetails(cloudId, uuid)
    return this.getAttachmentFileDetails(attachmentId, attachmentSourceId)
  }

  static async getCloudFileDetails (cloudId, uuid) {
    const { data } = await Channels.getChannelCloudByIdFileById(cloudId, uuid)
    return data
  }

  static async getAttachmentFileDetails (attachmentId, attachmentSourceId) {
    // re-fetch news items to get new download link for attachment
    const { data } = await Channels.getChannelNewsById(attachmentSourceId)
    const downloadUrl = data.attachments.find((el) => el.id === attachmentId)?.meta.download_uri

    if (!downloadUrl) {
      sentryConfig.addBreadcrumb({
        category: 'target attachmentId',
        message: `${attachmentId}`,
        level: 'warning'
      })
      sentryConfig.addBreadcrumb({
        category: 'attachmentSourceId',
        message: `${attachmentSourceId}`,
        level: 'warning'
      })
      // report the downloadUrl, id, and file type from data attachments
      const trimmedFileData = data.attachments?.map(attachment => ({
        id: attachment.id,
        extension: attachment.extension,
        file_type: attachment.file_type,
        type: attachment.type,
        meta: {
          download_uri: !!attachment.meta?.download_uri
        }
      }))
      sentryConfig.addBreadcrumb({
        category: 'data',
        message: JSON.stringify(trimmedFileData),
        level: 'warning'
      })
      sentryConfig.throwError('Attachment could not be re-fetched')
    }

    return { meta: { download_uri: downloadUrl, uri: downloadUrl } }
  }

  static async changeCollaboration (file) {
    const { data } = await Channels.putChannelCloudByIdFileByIChangeCollaboration(file.cloud_id, file.uuid, { is_collaborative: !file.is_collaborative })
    return data
  }

  static async renameFile (id, name, newName) {
    return Channels.putChannelCloudByIdRenameFile(id, { path: name, new_path: newName }, { hideSuccessMessages: true })
  }

  static async moveFile (cloud_id, files, destination) {
    const res = await Channels.putChannelCloudByIdMoveFiles(cloud_id, {
      files_uuid: files,
      parent: destination
    })
    await File.insertOrUpdate({ data: res.data })
  }

  async deleteFile () {
    await File.deleteFile(this.cloud_id, [this.uuid])
  }

  static async deleteFile (cloudId, uuids) {
    await Channels.deleteChannelCloudByIdDeleteFile(cloudId, { files: uuids }, { hideSuccessMessages: true })
    await File.delete(this.uuid)
  }
}

export default File
